import { React, useState, useEffect } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import PrecisionManufacturingRoundedIcon from '@mui/icons-material/PrecisionManufacturingRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import SolarPowerRoundedIcon from '@mui/icons-material/SolarPowerRounded';

import DayDatePicker from './dayDatePicker';
import { Alerta, PopupDialog } from '../components/alerta';
import SubmitButton from '../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../utils/dateUtils';
import { fetchFlask } from '../utils/fetchFlask';

const InserirGeracaoIndividual = () => {
    const [dateTime, setDateTime] = useState(null);
    const [error, setError] = useState(false);
    const [dados, setDados] = useState([]);
    const [site, setSite] = useState('');
    const [listaSites, setListaSites] = useState([]);
    const [usina, setUsina] = useState('');
    const [listaUsinas, setListaUsinas] = useState([]);
    const [inversor, setInversor] = useState('');
    const [listaInv, setListaInv] = useState([]);
    const [geracao, setGeracao] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [dataStr, setDataStr] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [textoDialog, setTextoDialog] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    useEffect(() => {
        async function pegarDados () {
            const response = await fetchFlask('pegarDados');
            if (response.status !== 'error') {
                const dadosFetch = response.dados;
                setDados(dadosFetch);
                const dadosSite = [...new Set(dadosFetch.map(item => item[0]))];
                setListaSites(dadosSite);
            } else {
                handleAlerta(response);
            }
        };
        pegarDados();
    }, []);

    const selectSite = (e) => {
        setSite(e.target.value);
        const dadosFiltrados = dados.filter(item => item[0] === e.target.value);
        const dadosusinas = [...new Set(dadosFiltrados.map(item => item[1]))];
        dadosusinas.sort();
        setListaUsinas(dadosusinas);
        setUsina('');
        setInversor('');
    };

    const selectUsinas = (e) => {
        setUsina(e.target.value);
        const dadosFiltrados = dados.filter(item => item[1] === e.target.value);
        const dadosInv = dadosFiltrados.map(item => item[2]);
        dadosInv.sort();
        setListaInv(dadosInv);
        setInversor('');
    };

    async function submitGeracao (e) {
        e.preventDefault();
        setIsSubmit(true);

        let data = '';
        if (dateTime) {
            setError(false);
            data = formatIsoDate(dateTime);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            data,
            inversores: inversor
        });

        try {
            const response = await fetchFlask('geracaoAtual', body);
            if (response.status === 'error') {
                handleAlerta(response);
            } else if (response.ger_atual === null) {
                setMessage('Não existe geração para esse dia, favor verificar as informações inseridas');
                setStatus('warning');
                setOpenAlerta(true);
            } else {
                setDataStr(data);
                handleDialog(response.ger_atual);
            }
        } finally {
            setIsSubmit(false);
        }
    };

    async function atualizarGeracao () {
        const body = JSON.stringify({
            data: dataStr,
            inversores: inversor,
            geracao
        });
        const response = await fetchFlask('atualizarGeracao', body);
        handleAlerta(response);
        if (response.status === 'success') {
            setDateTime(null);
            setSite('');
            setUsina('');
            setInversor('');
            setGeracao('');
        }
    }

    function handleDialog (gerAtual) {
        setTextoDialog('Geração atual é ' + gerAtual + ', deseja modificar para ' + geracao + '?');
        setOpenDialog(true);
    }

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    const dataLabel = formatDateLabelPtBr(dateTime);
    const completedFields = [
        dateTime,
        site,
        usina,
        inversor,
        geracao
    ].filter(Boolean).length;

    return (
        <>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PopupDialog
                titulo={'Atualizar Geração?'}
                texto={textoDialog}
                open={openDialog}
                handleClose={() => setOpenDialog(false)}
                funcao={atualizarGeracao}
            />

            <form action='' method='POST' id='formGeracao' onSubmit={submitGeracao} className='generation-entry-layout'>
                <Box className='generation-entry-workspace'>
                    <Box className='generation-entry-intro'>
                        <Box>
                            <h2>Atualização individual de geração</h2>
                        </Box>
                        <p>
                            Selecione a data, o ativo e a nova geração medida para validar a geração atual antes de atualizar.
                        </p>
                    </Box>

                    <Box className='generation-entry-panel'>
                        <Box className='generation-entry-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Data e site</strong>
                            </Box>
                        </Box>

                        <Box className='generation-entry-fields-grid'>
                            <FormControl className='generation-entry-date-control' error={error}>
                                <DayDatePicker
                                    id='data'
                                    label='Dia da geração'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>

                            <FormControl required className='generation-entry-select-control'>
                                <InputLabel id='labelSite'>Site</InputLabel>
                                <Select
                                    id='site'
                                    labelId='labelSite'
                                    label='Site'
                                    value={site}
                                    onChange={selectSite}
                                >
                                    {listaSites.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='generation-entry-panel'>
                        <Box className='generation-entry-section-heading'>
                            <FactoryRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Ativo de geração</strong>
                            </Box>
                        </Box>

                        <Box className='generation-entry-fields-grid'>
                            <FormControl required className='generation-entry-select-control'>
                                <InputLabel id='labelUsinas'>Usinas</InputLabel>
                                <Select
                                    id='usinas'
                                    labelId='labelUsinas'
                                    label='Usinas'
                                    value={usina}
                                    onChange={selectUsinas}
                                >
                                    {listaUsinas.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl required className='generation-entry-select-control'>
                                <InputLabel id='labelInversores'>Inversores</InputLabel>
                                <Select
                                    id='inversores'
                                    labelId='labelInversores'
                                    label='Inversores'
                                    value={inversor}
                                    onChange={(e) => setInversor(e.target.value)}
                                >
                                    {listaInv.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='generation-entry-panel'>
                        <Box className='generation-entry-section-heading'>
                            <QueryStatsRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Nova geração</strong>
                            </Box>
                        </Box>

                        <Box className='generation-entry-value-strip'>
                            <SolarPowerRoundedIcon />
                            <TextField
                                id='geracao'
                                label='Geração'
                                type='number'
                                inputProps={{ step: 0.01, min: -1 }}
                                required
                                onChange={(e) => setGeracao(e.target.value)}
                                value={geracao}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box className='generation-entry-summary'>
                    <Box className='generation-entry-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da geração</strong>
                        </Box>
                    </Box>

                    <Box className='generation-entry-summary-list'>
                        <Box>
                            <span>Data</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{dataLabel}</strong>
                        </Box>
                        <Box>
                            <span>Site</span>
                            <strong className={!site ? 'is-empty' : undefined}>{site || 'Não selecionado'}</strong>
                        </Box>
                        <Box>
                            <span>Usina</span>
                            <strong className={!usina ? 'is-empty' : undefined}>{usina || 'Não selecionada'}</strong>
                        </Box>
                        <Box>
                            <span>Inversor</span>
                            <strong className={!inversor ? 'is-empty' : undefined}>{inversor || 'Não selecionado'}</strong>
                        </Box>
                        <Box>
                            <span>Nova geração</span>
                            <strong className={!geracao ? 'is-empty' : undefined}>{geracao || 'Não informada'}</strong>
                        </Box>
                    </Box>

                    <Box className='generation-entry-attention'>
                        <PrecisionManufacturingRoundedIcon />
                        <p>
                            O envio consulta a geração atual antes de aplicar a atualização. Confirme no popup apenas se o valor estiver correto.
                        </p>
                    </Box>

                    <Box className='generation-entry-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/5 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Consultar e Atualizar
                    </SubmitButton>
                </Box>
            </form>
        </>
    );
};

export default InserirGeracaoIndividual;
